import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Constants } from 'src/app/shared/constant/constants';
import { IUserDeatils } from 'src/app/shared/models';
import { LoaderService, RequestService, SessionManagerService, ToastService } from 'src/app/shared/services/index';
import { UserAuthService } from './user-auth.service';
import { Router } from '@angular/router';
import { TermsConditionPopupComponent } from '../terms-condition-popup/terms-condition-popup.component';
import { encryptPassword } from 'src/app/shared/services/utils';
import { SubscriptionExpirePopupComponent } from '../login/subscription-expire-popup/subscription-expire-popup.component';
import { environment } from 'src/environments/environment';
import { FirebaseAuthService } from './firebase-auth.service';
import { Subject, Observable } from 'rxjs';
import { AccessCodeModalComponent } from '../sign-up/access-code-modal/access-code-modal.component';
import { UserTypes } from '@app/shared/enums/user.type';
import { determineCreatedByValue } from '@app/shared/helper/determine-created-by';
import { USER_HOME } from '@app/shared/constant/routes';
import { THEMES, THEME_IDS } from '@app/shared/services/theme/themes';
import { ThemeService } from '@app/shared/services/theme/theme.service';
import { IUserPreferences } from '@app/shared/models/IUserDetails';
@Injectable({
  providedIn: 'root',
})
export class AzureAuthService {
  public modalRef!: BsModalRef;
  socialData: { [key: string]: any } = {};
  private signupDataSubject: Subject<any> = new Subject<any>();
  signupData$: Observable<any> = this.signupDataSubject.asObservable();
  private baseUrl: string;

  constructor(
    private msalService: MsalService,
    private sessionManagerService: SessionManagerService,
    private userAuthService: UserAuthService,
    private httpClient: HttpClient,
    private requestService: RequestService,
    private loader: LoaderService,
    private flashMessage: ToastService,
    private router: Router,
    private readonly modalService: BsModalService,
    private firebaseAuthService: FirebaseAuthService,
    private themeService: ThemeService,
  ) {
    this.baseUrl = environment.baseApiURL;
  }

  //Sign in with Microsoft
  microsoftAuth(type: string, userType = '', parentEmail = '', selectedAccountType = ''): void {
    this.sessionManagerService.clearSession();
    this.sessionManagerService.clearMsalInteraction();
    this.msalService.loginPopup().subscribe({
      next: (result: AuthenticationResult) => {
        this.getProfile(result, Constants.SOCIAL_LOGIN_TYPES.MICROSOFT, type, userType, parentEmail, selectedAccountType);
      },
      error: (error: string) => {
        console.error('Microsoft auth error:', error);
        this.flashMessage.showError('Microsoft authentication failed. Please try again.');
        this.sessionManagerService.clearMsalInteraction();
      },
    });
  }

  getProfile(result: any, socialType: string, type: string, userType = '', parentEmail = '', selectedAccountType = '') {
    this.httpClient.get(environment.Azure.GetProfile).subscribe(resp => {
      this.setUserdata(resp, result.idToken, socialType, type, userType, parentEmail, selectedAccountType);
    });
  }

  //Set social data for social logins
  setUserdata(result: any, accessId: string, socialType: string, type: string, userType = '', parentEmail = '', selectedAccountType = '') {
    const priceId = this.sessionManagerService.getPriceId();
    this.socialData = {
      email: result.userPrincipalName,
      username: result.displayName,
      social: {
        accessKey: accessId,
        type: socialType,
      },
    };
    if (type === 'Studentlogin') {
      this.socialData['priceId'] = priceId ? priceId : 'free_trial';
      delete this.socialData.username;
      return this.userSocialLogin(this.socialData);
    } else if (type === 'Schoollogin') {
      delete this.socialData.username;
      return this.schoolSocialLogin(this.socialData);
    } else if (type === 'signup') {
      delete this.socialData.username;
      this.socialData['parentEmail'] = parentEmail || null;
      this.socialData['isTermsAndConditionChecked'] = true;
      this.socialData['isUserUnderAge'] = false;
      this.socialData['role'] = Constants.ROLE_IDS.USER;
      this.socialData['userType'] = userType === UserTypes.CHILD ? '1' : userType;
      this.socialData['createdBy'] = determineCreatedByValue(userType, selectedAccountType);

      return this.socialSignupAndShowAccessModal(this.socialData);
    } else if (type == 'schoolsigup') {
      return this.signupDataSubject.next(this.socialData);
    }
  }

  //User Social login api call
  userSocialLogin(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.ENDPOINTS.socialLogin, socialData).subscribe(
      async (res: any) => {
        this.loader.stop();
        if (res.data && res.data.activeToken) {
          localStorage.setItem('preferences', JSON.stringify(res.data?.userPreferences));
          await this.updateThemeToContrastTheme(res);

          const isSubscriptionMandatory = res.data.isSubscriptionMandatory == false ? '0' : '1';
          this.sessionManagerService.createSession(
            res.data.activeToken,
            res.data.role,
            res.data.id,
            res.data.levelId,
            res.data.levelNo,
            res.data.email,
          );
          this.sessionManagerService.setLoggedInData(res.data);
          this.sessionManagerService.setSubscriptionInfo(res.data?.subscriptionInfo);
          this.sessionManagerService.setPlanInfo(res.data?.planInfo);
          this.sessionManagerService.setIsSubcrpition(isSubscriptionMandatory);
          this.firebaseAuthService.loginWithToken(res.data.firebaseToken, res);
          this.sessionManagerService.setMicrosoftLogin();
          if (res.data.sessionUrl) {
            window.open(res.data.sessionUrl, '_self');
          } else {
            this.userAuthService.navigationBasedOnUser(res.data);
          }
        }
      },
      error => {
        this.loader.stop();
        if (error.error.code == Constants.ERROR_CODES.BAD_REQUEST) {
          const initialState = {
            token: error.error.data.activeToken,
            type: Constants.SOCIAL_LOGIN_TYPES.MICROSOFT_SIGNIN,
          };
          this.modalService.show(TermsConditionPopupComponent, {
            initialState,
          });
        } else if (error.error.status == Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(error.error.errorMessage);
          this.sessionManagerService.setPriceId('free_trial' as string);
          this.router.navigate([USER_HOME.URL]);
        } else if (error.error.status == Constants.ERROR_CODES.TRANSACTION) {
          const initialState = {
            headerText: error.error.errorMessage,
            email: this.socialData.email,
            password: encryptPassword('Hello world'),
            type: true,
          };
          this.modalService.show(SubscriptionExpirePopupComponent, {
            initialState,
          });
        } else {
          this.flashMessage.showError(error.error.errorMessage);
        }
      },
    );
  }

  //School Social login api call
  schoolSocialLogin(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.SCHOOL_ENDPOINTS.socialLogin, socialData).subscribe(
      (res: any) => {
        this.loader.stop();
        if (res.data && res.data.activeToken) {
          this.sessionManagerService.createSession(
            res.data.activeToken,
            res.data.role,
            res.data.id,
            res.data.levelId,
            res.data.levelNo,
            res.data.email,
          );
          this.sessionManagerService.setLoggedInData(res.data);
          this.sessionManagerService.setMicrosoftLogin();
          if (res.data.sessionUrl) {
            window.open(res.data.sessionUrl, '_self');
          } else {
            this.userAuthService.navigationBasedOnUser(res.data);
          }
        }
      },
      error => {
        this.loader.stop();
        if (error.error.code == Constants.ERROR_CODES.BAD_REQUEST) {
          const initialState = {
            token: error.error.data.activeToken,
            type: Constants.SOCIAL_LOGIN_TYPES.MICROSOFT_SIGNIN,
          };
          this.modalService.show(TermsConditionPopupComponent, {
            initialState,
          });
        } else if (error.error.status == Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(error.error.errorMessage);
          this.sessionManagerService.setPriceId('free_trial' as string);
          this.router.navigate([USER_HOME.URL]);
        } else if (error.error.status == Constants.ERROR_CODES.TRANSACTION) {
          const initialState = {
            headerText: error.error.errorMessage,
            email: this.socialData.email,
            password: encryptPassword('Hello world'),
            type: true,
          };
          this.modalService.show(SubscriptionExpirePopupComponent, {
            initialState,
          });
        } else {
          this.flashMessage.showError(error.error.errorMessage);
        }
      },
    );
  }

  async updateThemeToContrastTheme(res: any) {
    const { userPreferences, activeToken }: { userPreferences: IUserPreferences & { _id: string; userRole: number }; activeToken: string } = res.data;
    if (userPreferences && res.data?.userPreferences.theme) {
      if (userPreferences?.isUseContrastColors && (!userPreferences?.theme || userPreferences?.theme !== THEME_IDS.DEFAULT_HIGHCONTRAST)) {
        // * Update the theme to constrast theme.
        if (userPreferences?._id) {
          delete (userPreferences as Partial<typeof userPreferences>)._id;
        }
        try {
          const result: any = await this.httpClient
            .patch(
              `${this.baseUrl}${Constants.ENDPOINTS.userPreferencesSet}`,
              {
                ...userPreferences,
                theme: THEME_IDS.DEFAULT_HIGHCONTRAST,
                isUseContrastColors: false,
                userRole: userPreferences.userRole ? userPreferences.userRole : 1,
              },
              {
                headers: {
                  token: activeToken,
                  'X-Skip-Interceptor': 'true',
                },
              },
            )
            .toPromise();
          localStorage.setItem(
            'preferences',
            JSON.stringify({
              ...userPreferences,
              theme: result.data?.theme,
              isUseContrastColors: result.data?.isUseContrastColors,
            }),
          );

          const theme = THEMES.find(color => color.id === result.data?.theme) || THEMES[0];
          this.themeService.setColor(theme);
        } catch (error) {
          console.error('Error updating theme:', error);
        }
      } else {
        const theme = THEMES.find(color => color.id === res.data?.userPreferences.theme);
        this.themeService.setColor(theme! || THEMES[0]);
      }
    } else {
      this.themeService.setColor(THEMES[0]);
    }
  }

  socialSignupAndShowAccessModal(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.ENDPOINTS.socialSignup, socialData).subscribe(
      (res: any) => {
        this.loader.stop();
        if (res.data && res.data.activeToken) {
          this.modalService.show(AccessCodeModalComponent, {
            ignoreBackdropClick: true,
            initialState: {
              accessToken: res.data.activeToken,
            },
          });
        }
      },
      error => {
        this.loader.stop();
        if (error.error.status == Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(error.error.errorMessage);
        } else {
          this.flashMessage.showError(error.error.errorMessage);
        }
      },
    );
  }

  //Logout from Azure
  logOutfromAzure() {
    const value = this.sessionManagerService.getMicrosoftLogin();
    if (value == 'login_with_microsoft') {
      this.sessionManagerService.clearMicrosoftLogin();
      return this.msalService.logoutRedirect({
        // Return false if you would like to stop navigation after local logout
        onRedirectNavigate: () => false,
      });
    } else {
      return null;
    }
  }
}
